import type { ProjectSearchFile, ProjectSearchMatch, ProjectSearchResponse } from "@racler/protocol";
import type { RevealRange } from "./useOpenFiles.ts";

/** Pure logic of the global Project Search modal (kept free of React/DOM so it is unit-testable). */

export const SEARCH_DEBOUNCE_MS = 200;
export const SEARCH_MAX_FILES = 100;
export const SEARCH_MAX_MATCHES = 500;

/** One keyboard-selectable row: a file (name/path hit or file header) or one content match. */
export type SearchItem =
  | { kind: "file"; key: string; path: string }
  | { kind: "match"; key: string; path: string; match: ProjectSearchMatch };

/** Flat, ordered list of selectable rows. A file is selectable on its own when its name/path matched. */
export function flattenResults(files: readonly ProjectSearchFile[]): SearchItem[] {
  const items: SearchItem[] = [];
  for (const f of files) {
    if (f.nameMatch || f.matches.length === 0) items.push({ kind: "file", key: `f:${f.path}`, path: f.path });
    for (const m of f.matches) {
      items.push({ kind: "match", key: `m:${f.path}:${m.line}:${m.column}`, path: f.path, match: m });
    }
  }
  return items;
}

/** Next selected index, wrapping at both ends (-1 when there is nothing to select). */
export function moveSelection(current: number, delta: 1 | -1, length: number): number {
  if (length <= 0) return -1;
  if (current < 0) return delta === 1 ? 0 : length - 1;
  return (current + delta + length) % length;
}

/** What opening a result means for the editor: which file, and (content results) which range to select. */
export function navigationTarget(item: SearchItem): { path: string; range: RevealRange | null } {
  if (item.kind === "file") return { path: item.path, range: null };
  const { line, column, length } = item.match;
  return {
    path: item.path,
    range: { startLineNumber: line, startColumn: column, endLineNumber: line, endColumn: column + length },
  };
}

/** `[before, match, after]` of a snippet for highlighting (clamped; never throws on odd offsets). */
export function splitHighlight(text: string, start: number, length: number): [string, string, string] {
  const s = Math.min(Math.max(start, 0), text.length);
  const e = Math.min(s + Math.max(length, 0), text.length);
  return [text.slice(0, s), text.slice(s, e), text.slice(e)];
}

export function splitPath(path: string): { name: string; dir: string } {
  const i = path.lastIndexOf("/");
  return i < 0 ? { name: path, dir: "" } : { name: path.slice(i + 1), dir: path.slice(0, i + 1) };
}

export type SearchState =
  | { status: "idle" }
  | { status: "searching"; previous: ProjectSearchResponse | null }
  | { status: "done"; response: ProjectSearchResponse }
  | { status: "error"; message: string };

export interface SearchController {
  /** Schedules a search for `query` (debounced). Empty query resets to idle without any request. */
  setQuery(query: string): void;
  dispose(): void;
}

export interface SearchControllerDeps {
  search: (query: string, signal: AbortSignal) => Promise<ProjectSearchResponse>;
  onState: (state: SearchState) => void;
  debounceMs?: number;
  timers?: { set: (fn: () => void, ms: number) => unknown; clear: (id: unknown) => void };
}

/** Product-level message; raw errors / stack traces are never shown. */
export function searchErrorMessage(err: unknown): string {
  const code = (err as { code?: string } | null)?.code;
  if (code === "MACHINE_OFFLINE") return "Search is unavailable while the machine is offline.";
  if (code === "TIMEOUT") return "Search took too long. Try a more specific query.";
  return "Search failed. Please try again.";
}

/**
 * Debounces queries, aborts the in-flight search when a newer query arrives, and drops any result that is not from
 * the latest query (a stale response can never overwrite newer results, even if it ignores the abort signal).
 */
export function createSearchController(deps: SearchControllerDeps): SearchController {
  const debounceMs = deps.debounceMs ?? SEARCH_DEBOUNCE_MS;
  const timers = deps.timers ?? {
    set: (fn, ms) => setTimeout(fn, ms),
    clear: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
  };
  let generation = 0;
  let timer: unknown = null;
  let abort: AbortController | null = null;
  let last: ProjectSearchResponse | null = null;

  const cancelInFlight = () => {
    if (timer !== null) timers.clear(timer);
    timer = null;
    abort?.abort();
    abort = null;
  };

  return {
    setQuery(query) {
      const mine = ++generation;
      cancelInFlight();
      const q = query.trim();
      if (!q) {
        last = null;
        deps.onState({ status: "idle" });
        return;
      }
      deps.onState({ status: "searching", previous: last });
      timer = timers.set(() => {
        timer = null;
        const ctl = new AbortController();
        abort = ctl;
        deps.search(q, ctl.signal).then(
          (response) => {
            if (mine !== generation) return;
            last = response;
            deps.onState({ status: "done", response });
          },
          (err: unknown) => {
            if (mine !== generation) return;
            deps.onState({ status: "error", message: searchErrorMessage(err) });
          },
        );
      }, debounceMs);
    },
    dispose() {
      generation++;
      cancelInFlight();
    },
  };
}

/** Cmd+Shift+F (macOS) / Ctrl+Shift+F (Windows, Linux). Alt and the other modifier must not be held. */
export function isProjectSearchShortcut(
  e: Pick<KeyboardEvent, "key" | "code" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey">,
  isMac: boolean = /Mac|iPhone|iPad/i.test(typeof navigator === "undefined" ? "" : navigator.platform),
): boolean {
  if (!e.shiftKey || e.altKey) return false;
  const mod = isMac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
  return mod && (e.code === "KeyF" || e.key.toLowerCase() === "f");
}
